import { Auth } from '@aws-amplify/auth';
import { CometChat } from '@cometchat-pro/chat';
import includes from 'lodash/includes';

import { AppDispatch, GetRootState } from 'App';
import { isLoggedInSelector } from 'store/selectors/messagesSelectors';
import { userDataSelector } from 'store/selectors/userSelectors';
import { cleanStoreAction } from 'store/actions/commonActions';
import {
  ISetCurrentUserAction,
  ISetUserDataActionPayload,
  UserActionsEnum,
} from 'store/reducers/userReducer/userTypes';
import { engineAPI, setAuthorization } from 'api/axiosClient';
import Users from 'api/classes/Users';
import { checkEmailRequest } from 'api/requests/userRequests';
import { removeCookie } from 'utils/cookies';
import history from 'utils/history';
import { intercomShutdown } from 'utils/intercom';
import sendNotification, { ENotificationTypes } from 'utils/sendNotification';
import { getTokens } from 'utils/tokensActions';
import { COOKIE_KEYS, STORAGE_KEYS } from 'utils/constants/constants';
import { ERoutePaths } from 'types/routes/enums';
import { EAccountStatuses } from 'types/userTypes';

interface IGetUserSettingsAction {
  success: boolean,
  data?: {
    id: number,
    currency: string,
    region: string,
    language: number,
    cognitoUsername: string,
    approvalStatus: EAccountStatuses,
    denialCause: string | null,
    isAccountConfigured: boolean;
    isSupplierDetailsConfigured: boolean;
    isShippingConfigured: boolean;
    isReturnPoliciesConfigured: boolean;
    isOneStoreConnected: boolean;
    isStoreInfoFulfilled: boolean;
    isMinProducts: boolean;
    isSubmittedForReview: boolean;
    isTradingPolicyConfigured: boolean;
  };
  error?: any;
}

export const setLeftPanelAction = (payload: { isCollapsedLeftPanel: boolean; }) => {
  localStorage.setItem(STORAGE_KEYS.leftPanel, JSON.stringify(payload.isCollapsedLeftPanel));

  return ({ type: UserActionsEnum.SET_LEFT_PANEL, payload });
};

export const setThemeAction = (payload: { theme: string }) => ({ type: UserActionsEnum.SET_THEME, payload });

export const setCurrentUserAction = (payload: ISetUserDataActionPayload): ISetCurrentUserAction => ({
  type: UserActionsEnum.SET_CURRENT_USER,
  payload,
});

export const updateUserAction = (payload: any) => async(dispatch: AppDispatch, getStore: GetRootState) => {
  const { id: userId } = userDataSelector(getStore());
  const { success, data, error }: any = await Users.updateUser({ userId }, payload);

  if (success) {
    dispatch({ type: UserActionsEnum.UPDATE_USER, payload: data });
  } else {
    sendNotification(ENotificationTypes.Error, error);
  }
};

export const getUserSettingsAction = () => async(dispatch: AppDispatch) => {
  const { success, data, error }: IGetUserSettingsAction = await Users.getUserSettings();

  if (success && data) {
    const {
      isAccountConfigured,
      isSupplierDetailsConfigured,
      isShippingConfigured,
      isReturnPoliciesConfigured,
      isOneStoreConnected,
      isStoreInfoFulfilled,
      isMinProducts,
      isTradingPolicyConfigured,
      approvalStatus,
      ...rest
    } = data;
    const onBoardingSteps = [
      isAccountConfigured && isSupplierDetailsConfigured,
      isShippingConfigured && isReturnPoliciesConfigured,
      isTradingPolicyConfigured,
      isOneStoreConnected && isStoreInfoFulfilled,
      isMinProducts,
    ];
    const isOnboardingFinished = includes(['pending', 'approved', 'declined', 'final_declined'], approvalStatus);

    const payload = {
      ...rest,
      isUpdated: true,
      approvalStatus: approvalStatus || EAccountStatuses.Initial,
      isOnboardingFinished,
      onBoardingSteps,
    };

    dispatch({ type: UserActionsEnum.GET_USER_SETTINGS, payload });
  } else {
    sendNotification(ENotificationTypes.Error, error);
  }
};

export const logoutAction = (hardLogout?: boolean | undefined) =>
  async(dispatch: AppDispatch, getStore: GetRootState) => {
    const data = userDataSelector(getStore());
    const isLoggedIn = isLoggedInSelector(getStore());

    delete engineAPI.defaults.headers.common.Authorization;
    removeCookie(COOKIE_KEYS.idToken);
    removeCookie(COOKIE_KEYS.accessToken);
    removeCookie(COOKIE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.rememberSession);
    localStorage.removeItem(STORAGE_KEYS.userSelection);
    isLoggedIn && CometChat.logout();
    intercomShutdown();

    if (hardLogout) {
      dispatch(setCurrentUserAction({ data, isLoading: true }));
      await Auth.signOut();
      dispatch(cleanStoreAction());
    }
  };

export const userSelectionAction = (email: { email: string }) =>
  async(dispatch: AppDispatch, getStore: GetRootState) => {
    const isLoggedIn = isLoggedInSelector(getStore());
    const { success: checkSuccess, data: checkData } = await checkEmailRequest(email);

    if (checkSuccess) {
      if (checkData.type === 'UserNotFound') {
        sendNotification(ENotificationTypes.Error, checkData.message);

        return;
      }

      localStorage.setItem(STORAGE_KEYS.userSelection, JSON.stringify(checkData));
      const { idToken: token } = getTokens();

      setAuthorization({ token });
      const { success: userSuccess, data: userData }: any = await Users.getUsersList();

      if (userSuccess) {
        const currentUser = userDataSelector(getStore());

        const userSelection = {
          ...userData[0],
          permissions: [...(userData[0].privileges || []), ...currentUser.permissions],
          emailVerified: true,
        };

        isLoggedIn && CometChat.logout();
        localStorage.setItem(STORAGE_KEYS.userSelection, JSON.stringify(userSelection));
        history.push({ pathname: ERoutePaths.Root });
        document.location.reload();
      }
    }
  };
